import { MODULES, ROLES, ROUTES } from "shared/config";
import RequireAuthentication from "features/Auth/hoc/RequireAuthentication";
import RequireAuthorization from "features/Auth/hoc/RequireAuthorization";
import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import RootBoundary from "shared/lib/boundaries/RootBoundary";
import CorporateCardsManagementPage from "./CorporateCardsManagment/CorporateCardsManagement.page";
import CorporateStatementUploadPage from "./CorporateStatementsUploadPage/CorporateStatementUpload.page";
import RequireModuleAuthorization from "features/Auth/hoc/RequireModuleAuthorization";
import CorporateCardHolderPanelPage from "./CorporateCardHolderPanelPage/CorporateCardHolderPanelPage";
import CorporateRequestsPage from "./CorporateRequestsPage/CorporateRequestsPage";
import CorporateStatementDetailsPage from "./CorporateStatementDetailsPage/CorporateStatementsDetailsPage";

const HomePage = lazy(() => import("./HomePage/HomePage"));
const RequestsPage = lazy(() => import("./RequestsPage/RequestsPage"));
const CardRequestsPage = lazy(() =>
  import("./CardRequestsPage/CardRequestsPage")
);
const CardStatementDetailsPage = lazy(() =>
  import("./CardStatementDetailsPage/CardStatementsDetailsPage")
);
const SubmitExpenseRequestPage = lazy(() =>
  import("./SubmitExpenseRequestPage/SubmitExpenseRequestPage")
);
const ProfilePage = lazy(() => import("./ProfilePage/ProfilePage"));
const FaqPage = lazy(() => import("./FaqPage/FaqPage"));
const ContactPage = lazy(() => import("./ContactPage/ContactPage"));
const CardsManagementPage = lazy(() =>
  import("./CardsManagementPage/CardsManagementPage")
);
const StatementUploadPage = lazy(() =>
  import("./StatementUploadPage/StatementUploadPage")
);
const RequestHistoryPage = lazy(() =>
  import("./RequestHistoryPage/RequestHistoryPage")
);
const CostsAuditPage = lazy(() => import("./CostsAuditPage/CostsAuditPage"));
const CardHolderPanelPage = lazy(() =>
  import("./CardHolderPanelPage/CardHolderPanelPage")
);
const LocalAdmingPage = lazy(() => import("./LocalAdmingPage/LocalAdminPage"));
const RequestsManagementPage = lazy(() =>
  import("./RequestsManagementPage/RequestsManagementPage")
);
const UsersManagementPage = lazy(() =>
  import("./UsersManagementPage/UsersManagementPage")
);
const ApPanelPage = lazy(() => import("./ApPanelPage/ApPanelPage"));
const AccountsPayableQueuePage = lazy(() =>
  import("./AccountsPayableQueuePage/AccountsPayableQueuePage")
);
const ApAdvancePaymentsPage = lazy(() =>
  import("./ApAdvancePaymentsPage/ApAdvancePaymentsPage")
);

const SapPreviewPage = lazy(() => import("./SapPreviewPage/SapPreviewPage"));
const CostGroupPage = lazy(() => import("./CostGroupPage/CostGroupPage"));
const UnauthorizedPage = lazy(() =>
  import("./UnauthorizedPage/UnauthorizedPage")
);
const SubmitTravelRequestPage = lazy(() =>
  import("./SubmitTravelRequestPage/SubmitTravelRequestPage")
);
const RequestDetailsPage = lazy(() =>
  import("./RequestDetailsPage/RequestDetailsPage")
);
const MealsCalendarPage = lazy(() =>
  import("./MealsCalendarPage/MealsCalendarPage")
);

const LocalHRPage = lazy(() => import("./LocalHRPage/LocalHRPage"));
const TravelAcceptorQueuePage = lazy(() =>
  import("./TravelAcceptorQueuePage/TravelAcceptorQueuePage")
);

const router = createBrowserRouter([
  {
    element: <RequireAuthentication />,
    errorElement: <RootBoundary />,
    children: [
      {
        path: ROUTES.home,
        element: <HomePage />,
      },
      {
        path: ROUTES.request.queue_with_parameter,
        element: <RequestsPage />,
      },
      {
        element: <RequireModuleAuthorization module={MODULES.CREDIT_CARDS} />,
        children: [
          {
            element: <CardRequestsPage />,
            path: ROUTES.cards.card_requests_queue_with_parameter,
          },
          {
            element: <CardStatementDetailsPage />,
            path: ROUTES.cards.card_request_details_with_parameter,
          },
        ],
      },
      {
        element: (
          <RequireModuleAuthorization module={MODULES.CORPORATE_CARDS} />
        ),
        children: [
          {
            element: <CorporateRequestsPage />,
            path: ROUTES.cards.corporate_cards_requests_queue_with_parameter,
          },
          {
            element: <CorporateStatementDetailsPage />,
            path: ROUTES.cards.corporate_cards_request_details_with_parameter,
          },
        ],
      },
      {
        element: <SubmitExpenseRequestPage />,
        path: ROUTES.request.new_expense,
      },
      {
        element: <ProfilePage />,
        path: ROUTES.user_profile,
      },
      {
        element: <MealsCalendarPage />,
        path: ROUTES.request.meals_calendar_with_parameter,
      },
      {
        element: <FaqPage />,
        path: ROUTES.faq,
      },
      {
        element: <ContactPage />,
        path: ROUTES.contacts,
      },
      {
        element: (
          <RequireAuthorization allowedRoles={[ROLES.LOCAL_ADMINISTRATOR]} />
        ),
        children: [
          { element: <LocalAdmingPage />, path: ROUTES.local_admin.queue },
          {
            element: <UsersManagementPage />,
            path: ROUTES.local_admin.users_management_queue_with_parameter,
          },
          {
            element: <RequestsManagementPage />,
            path: ROUTES.local_admin.requests_management_with_parameter,
          },
          {
            element: (
              <RequireModuleAuthorization
                module={MODULES.CREDIT_CARDS}
                isLocalAdminModule
              />
            ),
            children: [
              {
                element: <CardsManagementPage />,
                path: ROUTES.local_admin.cards_management,
              },
              {
                element: <StatementUploadPage />,
                path: ROUTES.local_admin.card_statement_upload,
              },
              {
                element: <CardHolderPanelPage />,
                path: ROUTES.local_admin.card_holders,
              },
              {
                element: <CostsAuditPage />,
                path: ROUTES.local_admin.costs_audit,
              },
              {
                element: <RequestHistoryPage />,
                path: ROUTES.local_admin.requests_history,
              },
            ],
          },
          {
            element: (
              <RequireModuleAuthorization
                module={MODULES.CORPORATE_CARDS}
                isLocalAdminModule
              />
            ),
            children: [
              {
                element: <CorporateStatementUploadPage />,
                path: ROUTES.local_admin.corporate_card_statement_upload,
              },
              // {
              //   element: <CorporateRequestHistoryPage />,
              //   path: "/local-admin/corporate-request-history",
              // },
              // {
              //   element: <CorporateCostsAuditPage />,
              //   path: "/local-admin/corporate-costs-audit",
              // },
              {
                element: <CorporateCardHolderPanelPage />,
                path: ROUTES.local_admin.corporate_card_holders,
              },
              {
                element: <CorporateCardsManagementPage />,
                path: ROUTES.local_admin.corporate_cards_management,
              },
            ],
          },
        ],
      },
      {
        element: (
          <RequireAuthorization allowedRoles={[ROLES.ACCOUNTS_PAYABLE]} />
        ),
        children: [
          {
            element: <ApPanelPage />,
            path: ROUTES.accounts_payable.panel,
          },
          {
            element: <AccountsPayableQueuePage />,
            path: ROUTES.accounts_payable.queue_with_parameter,
          },
          {
            element: <SapPreviewPage />,
            path: ROUTES.accounts_payable.sap_preview_with_parameter,
          },
          {
            element: <ApAdvancePaymentsPage />,
            path: ROUTES.accounts_payable.advance_payments_queue,
          },
        ],
      },
      {
        element: <RequireAuthorization allowedRoles={[ROLES.COST_GROUP]} />,
        children: [
          {
            element: <CostGroupPage />,
            path: ROUTES.cost_group.queue,
          },
        ],
      },
      {
        element: (
          <RequireAuthorization checkTravelRequestingAuthorization={true} />
        ),
        children: [
          {
            element: <SubmitTravelRequestPage />,
            path: ROUTES.request.new_travel,
          },
        ],
      },
      {
        element: <RequestDetailsPage />,
        path: ROUTES.request.details_with_parameter,
      },
      {
        element: <RequireAuthorization allowedRoles={[ROLES.LOCAL_HR]} />,
        children: [
          {
            element: <LocalHRPage />,
            path: ROUTES.local_hr.queue,
          },
        ],
      },
      {
        element: (
          <RequireAuthorization
            allowedRoles={[ROLES.ADMINISTRATION]}
            allowedCountries={["PL"]}
          />
        ),
        children: [
          {
            element: <TravelAcceptorQueuePage />,
            path: ROUTES.travel_acceptor.queue,
          },
        ],
      },
      { element: <UnauthorizedPage />, path: ROUTES.unauthorized },
    ],
  },
]);

export default router;
